import React, { KeyboardEvent, ReactNode, useState } from "react"
import { Link } from "gatsby"
import {
  ConsentBanner,
  ConsentDialog,
  ConsentWidget,
  useHeadlessConsentUI
} from "@c15t/react"
import type { HeadlessConsentSurfaceAction } from "@c15t/react"
import Button from "components/Forms/Button"
import { CURRENT_REGION } from "lib/environment"
import {
  buildBannerDescriptionParts,
  consentActionLabels
} from "cookiesConsent/infrastructure/c15t/config"
import "@c15t/react/styles.css"
import "cookiesConsent/infrastructure/c15t/ui/overrides.css"

interface ConsentAction {
  label: string
  run: () => void
}

type ConsentActions = Partial<
  Record<HeadlessConsentSurfaceAction, ConsentAction>
>

interface ActionRenderProps {
  key: string
  isPrimary: boolean
}

const renderActionWith =
  (actions: ConsentActions) =>
  (
    action: HeadlessConsentSurfaceAction,
    { key, isPrimary }: ActionRenderProps
  ): ReactNode => {
    const config = actions[action]

    if (config) {
      return (
        <Button
          key={key}
          text={config.label}
          onClick={config.run}
          secondary={!isPrimary}
          fluid
        />
      )
    } else {
      return null
    }
  }

const BannerDescription = () => {
  const parts = buildBannerDescriptionParts(CURRENT_REGION)

  return (
    <>
      {parts.prefix}
      <Link to="/pages/privacy/" className="font-medium underline">
        {parts.linkText}
      </Link>
      {parts.suffix}
    </>
  )
}

const ConsentUi = () => {
  const {
    performBannerAction,
    performDialogAction,
    openDialog,
    saveCustomPreferences,
    closeUI
  } = useHeadlessConsentUI()

  const [openCategory, setOpenCategory] = useState(``)

  const bannerActions: ConsentActions = {
    accept: {
      label: consentActionLabels.acceptAll,
      run: () => performBannerAction(`accept`)
    },
    reject: {
      label: consentActionLabels.rejectAll,
      run: () => performBannerAction(`reject`)
    },
    customize: { label: consentActionLabels.customize, run: () => openDialog() }
  }

  const dialogActions: ConsentActions = {
    reject: {
      label: consentActionLabels.rejectAll,
      run: () => performDialogAction(`reject`)
    },
    customize: {
      label: consentActionLabels.save,
      run: () => saveCustomPreferences()
    }
  }

  const closeDialogOnEscape = (event: KeyboardEvent<HTMLDialogElement>) => {
    const keyActions: Partial<Record<string, () => void>> = { Escape: closeUI }

    keyActions[event.key]?.()
  }

  return (
    <>
      <ConsentBanner.Root scrollLock trapFocus>
        <ConsentBanner.Card>
          <ConsentBanner.Header>
            <ConsentBanner.Title />
            <ConsentBanner.Description legalLinks={null}>
              <BannerDescription />
            </ConsentBanner.Description>
          </ConsentBanner.Header>
          <ConsentBanner.PolicyActions
            renderAction={renderActionWith(bannerActions)}
          />
        </ConsentBanner.Card>
      </ConsentBanner.Root>

      <ConsentDialog.Root
        scrollLock
        trapFocus
        role="dialog"
        aria-modal="true"
        onKeyDown={closeDialogOnEscape}
      >
        <ConsentDialog.Card>
          <ConsentDialog.Header>
            <ConsentDialog.HeaderTitle />
            <ConsentDialog.HeaderDescription legalLinks={null} />
          </ConsentDialog.Header>
          <ConsentDialog.Content>
            <ConsentWidget.Root useProvider>
              <ConsentWidget.Accordion
                type="single"
                value={openCategory}
                onValueChange={value =>
                  setOpenCategory(
                    Array.isArray(value) ? value[0] ?? `` : value ?? ``
                  )
                }
              >
                <ConsentWidget.AccordionItems />
              </ConsentWidget.Accordion>
              <ConsentWidget.PolicyActions
                renderAction={renderActionWith(dialogActions)}
              />
            </ConsentWidget.Root>
          </ConsentDialog.Content>
        </ConsentDialog.Card>
      </ConsentDialog.Root>
    </>
  )
}

export default ConsentUi
